import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { localDateStr } from '../utils/dateUtils';
import Modal from '../components/common/Modal';
import { useAuth } from '../context/AuthContext';

const TYPE_ICONS = {
  call: '📞',
  meeting: '🤝',
  email: '📧',
  visit: '🚗',
  follow_up: '🔔',
  demo: '💡',
  other: '📌',
};

const TYPE_LABELS = {
  call: 'Call',
  meeting: 'Meeting',
  email: 'Email',
  visit: 'Visit',
  follow_up: 'Follow-up',
  demo: 'Demo',
  other: 'Other',
};

const PRIORITY_COLORS = {
  high: { bg: '#fee2e2', color: '#dc2626' },
  medium: { bg: '#fef3c7', color: '#d97706' },
  low: { bg: '#dcfce7', color: '#16a34a' },
};

const STATUS_COLORS = {
  pending: { bg: '#dbeafe', color: '#2563eb' },
  completed: { bg: '#dcfce7', color: '#16a34a' },
  cancelled: { bg: '#f3f4f6', color: '#6b7280' },
  rescheduled: { bg: '#fef3c7', color: '#d97706' },
};

const defaultForm = {
  type: 'call',
  title: '',
  description: '',
  due_date: '',
  due_time: '',
  priority: 'medium',
  client_id: '',
  customer_id: '',
  assigned_to: '',
  notes: '',
  status: 'pending',
  is_recurring: false,
  recurrence_type: 'weekly',
  recurrence_interval: 1,
  recurrence_end_date: '',
};

export default function Activities() {
  const { user, isAdmin, isManager } = useAuth();
  const canManage = isAdmin || isManager;

  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, overdue: 0, due_today: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', priority: '', type: '' });
  const [modal, setModal] = useState({ open: false, mode: 'add', item: null });
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [freezeConfirm, setFreezeConfirm] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/activities/stats');
      setStats(data);
    } catch (_) {}
  }, []);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.type) params.type = filters.type;
      const { data } = await api.get('/activities', { params });
      setActivities(data);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadActivities(); }, [loadActivities]);

  useEffect(() => {
    api.get('/clients', { params: { limit: 500, approval_status: 'approved' } }).then(r => setClients(r.data.data || [])).catch(() => {});
    api.get('/customers', { params: { limit: 500 } }).then(r => setCustomers(r.data.data || [])).catch(() => {});
    if (canManage) {
      api.get('/users').then(r => setUsers(r.data.data || [])).catch(() => {});
    }
  }, [canManage]);

  const openAdd = () => {
    setForm({ ...defaultForm, assigned_to: user?.id || '' });
    setError('');
    setModal({ open: true, mode: 'add', item: null });
  };

  const openEdit = (item) => {
    setForm({
      type: item.type || 'call',
      title: item.title || '',
      description: item.description || '',
      due_date: item.due_date ? item.due_date.slice(0, 10) : '',
      due_time: item.due_time || '',
      priority: item.priority || 'medium',
      client_id: item.client_id || '',
      customer_id: item.customer_id || '',
      assigned_to: item.assigned_to || '',
      notes: item.notes || '',
      status: item.status || 'pending',
      is_recurring: !!item.is_recurring,
      recurrence_type: item.recurrence_type || 'weekly',
      recurrence_interval: item.recurrence_interval || 1,
      recurrence_end_date: item.recurrence_end_date ? item.recurrence_end_date.slice(0, 10) : '',
    });
    setError('');
    setModal({ open: true, mode: 'edit', item });
  };

  const handleSave = async () => {
    if (!form.title || !form.due_date) { setError('Title and due date are required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (!payload.client_id) delete payload.client_id;
      if (!payload.customer_id) delete payload.customer_id;
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.due_time) delete payload.due_time;

      if (modal.mode === 'add') {
        await api.post('/activities', payload);
      } else {
        await api.put(`/activities/${modal.item.id}`, payload);
      }
      setModal({ open: false, mode: 'add', item: null });
      loadActivities();
      loadStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const handleMarkComplete = async () => {
    setSaving(true); setError('');
    try {
      await api.put(`/activities/${modal.item.id}`, { status: 'completed' });
      setModal({ open: false });
      loadActivities();
      loadStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update.');
    } finally { setSaving(false); }
  };

  const handleFreeze = async () => {
    try {
      await api.delete(`/activities/${freezeConfirm.id}`);
      setFreezeConfirm(null);
      loadActivities();
      loadStats();
    } catch (_) {}
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const today = localDateStr();
  const isOverdue = (item) => item.status === 'pending' && item.due_date && item.due_date.slice(0, 10) < today;

  const statCards = [
    { label: 'Total', value: stats.total, color: 'var(--primary)', bg: 'var(--primary-lighter)' },
    { label: 'Pending', value: stats.pending, color: '#2563eb', bg: '#dbeafe' },
    { label: 'Completed', value: stats.completed, color: '#16a34a', bg: '#dcfce7' },
    { label: 'Overdue', value: stats.overdue, color: '#dc2626', bg: '#fee2e2' },
    { label: 'Due Today', value: stats.due_today, color: '#d97706', bg: '#fef3c7' },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Activities</h1>
          <p className="page-subtitle">Track calls, meetings, follow-ups and scheduled tasks</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Activity
        </button>
      </div>

      {/* Stats Bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        {statCards.map(s => (
          <div key={s.label} className="card" style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.color }}>{s.value ?? 0}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-control form-select" style={{ width: 150 }} value={filters.status}
            onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}>
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="rescheduled">Rescheduled</option>
          </select>
          <select className="form-control form-select" style={{ width: 150 }} value={filters.priority}
            onChange={e => setFilters(p => ({ ...p, priority: e.target.value }))}>
            <option value="">All Priority</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select className="form-control form-select" style={{ width: 160 }} value={filters.type}
            onChange={e => setFilters(p => ({ ...p, type: e.target.value }))}>
            <option value="">All Types</option>
            {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {(filters.status || filters.priority || filters.type) && (
            <button className="btn btn-ghost btn-sm" onClick={() => setFilters({ status: '', priority: '', type: '' })}>Clear</button>
          )}
        </div>
      </div>

      {/* Activity Cards */}
      {loading ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
      ) : activities.length === 0 ? (
        <div className="card">
          <div className="empty-state"><p>No activities found. Add your first activity.</p></div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {activities.map(item => {
            const overdue = isOverdue(item);
            const prColors = PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.medium;
            const stColors = STATUS_COLORS[item.status] || STATUS_COLORS.pending;
            return (
              <div key={item.id} className="card" style={{
                padding: '14px 18px',
                background: overdue ? '#fff5f5' : undefined,
                borderLeft: overdue ? '3px solid #dc2626' : '3px solid transparent',
              }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '1.6rem', lineHeight: 1, flexShrink: 0, marginTop: 2 }}>
                    {TYPE_ICONS[item.type] || TYPE_ICONS.other}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)' }}>{item.title}</span>
                      {item.is_recurring && (
                        <span title={`Repeats ${item.recurrence_type}`} style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 8, background: '#ede9fe', color: '#7c3aed', fontWeight: 600 }}>🔁 {item.recurrence_type}</span>
                      )}
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: prColors.bg, color: prColors.color }}>
                        {item.priority}
                      </span>
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: stColors.bg, color: stColors.color }}>
                        {item.status}
                      </span>
                      {overdue && (
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: '#fee2e2', color: '#dc2626' }}>Overdue</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      <span>
                        <strong>Due:</strong>{' '}
                        {item.due_date ? new Date(item.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        {item.due_time ? ` ${item.due_time.slice(0, 5)}` : ''}
                      </span>
                      {(item.client_name || item.customer_name) && (
                        <span><strong>For:</strong> {item.client_name || item.customer_name}</span>
                      )}
                      {item.assigned_to_name && (
                        <span><strong>Assigned:</strong> {item.assigned_to_name}</span>
                      )}
                      <span><strong>Type:</strong> {TYPE_LABELS[item.type] || item.type}</span>
                    </div>
                    {item.description && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 4 }}>{item.description}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(item)}>Edit</button>
                    {canManage && (
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => setFreezeConfirm(item)}>
                        Freeze
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={modal.open}
        onClose={() => setModal({ open: false, mode: 'add', item: null })}
        title={modal.mode === 'add' ? 'Add Activity' : 'Edit Activity'}
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal({ open: false, mode: 'add', item: null })}>Cancel</button>
            {modal.mode === 'edit' && modal.item?.status === 'pending' && (
              <button className="btn btn-secondary" style={{ background: '#dcfce7', color: '#16a34a', border: 'none' }}
                onClick={handleMarkComplete} disabled={saving}>
                Mark Complete
              </button>
            )}
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : 'Save Activity'}
            </button>
          </>
        }
      >
        {error && <div className="alert alert-error">{error}</div>}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-control" value={form.title} onChange={e => f('title', e.target.value)} placeholder="Activity title" />
          </div>
          <div className="form-group">
            <label className="form-label">Type</label>
            <select className="form-control form-select" value={form.type} onChange={e => f('type', e.target.value)}>
              {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Due Date *</label>
            <input type="date" className="form-control" value={form.due_date} onChange={e => f('due_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Due Time</label>
            <input type="time" className="form-control" value={form.due_time} onChange={e => f('due_time', e.target.value)} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Priority</label>
            <select className="form-control form-select" value={form.priority} onChange={e => f('priority', e.target.value)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          {modal.mode === 'edit' && (
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control form-select" value={form.status} onChange={e => f('status', e.target.value)}>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="rescheduled">Rescheduled</option>
              </select>
            </div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-control" rows={2} value={form.description} onChange={e => f('description', e.target.value)} placeholder="Activity details..." />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Client (optional)</label>
            <select className="form-control form-select" value={form.client_id} onChange={e => f('client_id', e.target.value)}>
              <option value="">-- None --</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Customer (optional)</label>
            <select className="form-control form-select" value={form.customer_id} onChange={e => f('customer_id', e.target.value)}>
              <option value="">-- None --</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        {canManage && (
          <div className="form-group">
            <label className="form-label">Assign To</label>
            <select className="form-control form-select" value={form.assigned_to} onChange={e => f('assigned_to', e.target.value)}>
              <option value="">-- Unassigned --</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-control" rows={2} value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Additional notes..." />
        </div>

        {/* Recurrence */}
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
            <input type="checkbox" checked={!!form.is_recurring} onChange={e => f('is_recurring', e.target.checked)} />
            Recurring Activity
          </label>
        </div>
        {form.is_recurring && (
          <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Frequency</label>
              <select className="form-control form-select" value={form.recurrence_type} onChange={e => f('recurrence_type', e.target.value)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Every</label>
              <input className="form-control" type="number" min="1" max="30" value={form.recurrence_interval}
                onChange={e => f('recurrence_interval', e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">End Date</label>
              <input className="form-control" type="date" value={form.recurrence_end_date} onChange={e => f('recurrence_end_date', e.target.value)} />
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!freezeConfirm} onClose={() => setFreezeConfirm(null)} title="Freeze Activity"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setFreezeConfirm(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={handleFreeze}>Freeze</button>
        </>}
      >
        <p>Freeze <strong>{freezeConfirm?.title}</strong>? It will become read-only. You can unfreeze it later.</p>
      </Modal>
    </div>
  );
}
